import * as React from 'react';
import { PanelLeftCloseIcon, PanelLeftOpenIcon, type LucideIcon } from 'lucide-react';
import { NavLink, useLocation } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

export type AdministrationMenuItem = {
  label: string;
  icon: LucideIcon;
  to: string;
  kind?: 'route' | 'anchor';
};

export function AdministrationSideMenu({
  label,
  items,
  activeTo
}: {
  label: string;
  items: readonly AdministrationMenuItem[];
  activeTo?: string;
}) {
  const [collapsed, setCollapsed] = React.useState(false);
  const location = useLocation();

  return (
    <aside
      className={cn(
        'bg-sidebar text-sidebar-foreground border-border w-full shrink-0 rounded-lg border transition-[width] duration-200 motion-reduce:transition-none md:self-start',
        collapsed ? 'md:w-14' : 'md:w-56'
      )}
    >
      <div className={cn('flex items-center px-2 pt-2', collapsed ? 'justify-center' : 'justify-between')}>
        {!collapsed ? <p className="text-muted-foreground truncate px-2 text-xs font-medium tracking-wide uppercase">{label}</p> : null}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => setCollapsed((value) => !value)}
              aria-label={collapsed ? `Expand ${label} menu` : `Collapse ${label} menu`}
              aria-expanded={!collapsed}
              className="shrink-0"
            >
              {collapsed ? <PanelLeftOpenIcon className="size-4" /> : <PanelLeftCloseIcon className="size-4" />}
            </Button>
          </TooltipTrigger>
          <TooltipContent side="right">{collapsed ? 'Expand menu' : 'Collapse menu'}</TooltipContent>
        </Tooltip>
      </div>
      <nav aria-label={label} className={cn('flex flex-col gap-0.5 p-2', collapsed && 'max-md:hidden')}>
        {items.map((item) => {
          const linkClass = (active: boolean) => cn(
            'focus-visible:ring-ring flex items-center gap-2 rounded-md px-2 py-1.5 text-sm transition-colors focus-visible:ring-2 focus-visible:outline-none',
            active ? 'bg-accent text-accent-foreground font-medium' : 'text-muted-foreground hover:bg-accent/60 hover:text-accent-foreground',
            collapsed && 'justify-center'
          );
          const link = item.kind === 'anchor'
            ? <a href={item.to} aria-label={collapsed ? item.label : undefined} aria-current={(activeTo ?? location.hash) === item.to ? 'location' : undefined} className={linkClass((activeTo ?? location.hash) === item.to)}><item.icon aria-hidden className="size-4 shrink-0" />{!collapsed && <span className="truncate">{item.label}</span>}</a>
            : <NavLink to={item.to} aria-label={collapsed ? item.label : undefined} className={({ isActive }) => linkClass(isActive)}><item.icon aria-hidden className="size-4 shrink-0" />{!collapsed && <span className="truncate">{item.label}</span>}</NavLink>;
          return collapsed
            ? <Tooltip key={item.to}><TooltipTrigger asChild>{link}</TooltipTrigger><TooltipContent side="right">{item.label}</TooltipContent></Tooltip>
            : <React.Fragment key={item.to}>{link}</React.Fragment>;
        })}
      </nav>
    </aside>
  );
}
